import { ServiceBase } from "@app/shared";
import { NetworkAssessmentAnalysisType } from "../projects/enumerators";
import { AvailableFilterCriteria, ReportsUIModel } from "../reports/reportsModel";
import { AdequacyAggregateType, NetworkProviderFilterType } from "./enumerations";
import { ClosestProviderReportModel, ExportAccessReportsUsageModel, ExportAccuracyActionableProvidersModel, ExportAccuracyAggregatesModel, ExportAccuracySummariesModel, ExportAdequacyAggregatesModel, ExportAdequacyServicingProvidersModel, ExportAdequacySummariesModel, ExportAttestationAggregatesModel, ExportAttestationServicingProvidersModel, ExportAttestationSummariesModel, ExportCompositeAggregatesModel, ExportCompositeServicingProvidersModel, ExportCompositeSummariesModel, ExportRecommendedActionReasonModel, ExportStatusReportModel, ExportSummariesDetailProvidersRequestModel, ExportUserLoginStatisticsRequestModel, ReportSettingsModel, VolumeServicingProvidersRequestModel, VolumeSummariesRequestModel, ZipSummariesReportModel, ExportCCIIOProviderModel, SharedUserModel, VolumeZipSummariesRequestModel, ExportPciCrossProjectRequestModel, ExportBenchmarkingDashboardDetailsModel, ExportZipAnalysisRequestModel, ExportAdequacyVolumeAggregateRequestModel, ExportAdequacySpecialtySummaryProjectModel, VolumeAdequacySpecialtySummariesRequestModel, ExportBulkSpecialtyGroupExceptionsModel, ExportProvidersModel, ExportCCIIODataValidationsModel } from "./exportModels";
import { PciDashboardFiltersPayload } from "@app/projects/network-assessment/pci-dashboard/shared";
import { querystringify } from "@app/shared/utility";
import { ColumnConfigurationPresetOption } from "@app/report-customization/build-reports/interfaces/columnConfigurationPresetOption";
import { ExportColumn } from "@app/report-customization/build-reports/interfaces/exportColumns";
import { ColumnConfigurationPreset } from "@app/report-customization/build-reports/interfaces/columnConfigurationPreset";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ReportPresetsApiResponse } from "@app/report-customization/manage-presets/interfaces/reportPresetsApiResponse";
import { ReportPreset } from "@app/report-customization/build-reports/interfaces/reportPreset";

/** Manages interaction with the export API. */
export default class ExportService extends ServiceBase {

  // Access Report
  public async postExportAccessUsers(model: ExportAccessReportsUsageModel): Promise<Blob> {
    return await this.makePost<Blob>(`access-usage-report`, model, `${this.apiEndpoint}/exports`, false, "application/json", true, { responseType: "blob" });
  }

  // Accuracy
  public async getExportAccuracyRecommendedActionReasons(projectId: number, projectRunConfigurationId: number, inspectorUids: string[]): Promise<ExportRecommendedActionReasonModel[]> {
    if (inspectorUids && inspectorUids.length > 0) {
      return await this.makeGet<ExportRecommendedActionReasonModel[]>(`exports/accuracy-recommended-action-reasons-inspector-uids`, { inspectorUids: inspectorUids.join(";") });
    }

    return await this.makeGet<ExportRecommendedActionReasonModel[]>(`exports/accuracy-recommended-action-reasons`, { projectId, projectRunConfigurationId });
  }
  public async postExportAccuracyAggregates(model: ExportAccuracyAggregatesModel): Promise<number> {
    return await this.makePost<number>(`exports/accuracy-aggregates`, model);
  }

  public async postExportAccuracySummaries(model: ExportAccuracySummariesModel): Promise<number> {
    return await this.makePost<number>(`exports/accuracy-summaries`, model);
  }

  public async postExportAccuracyActionableProviders(model: ExportAccuracyActionableProvidersModel): Promise<number> {
    return await this.makePost<number>(`exports/accuracy-actionable-providers`, model);
  }

  public async postExportAccuracyStatusReport(model: ExportStatusReportModel): Promise<number> {
    return await this.makePost<number>(`exports/accuracy-status-report`, model);
  }

  // Adequacy
  public async postExportAdequacyAggregates(model: ExportAdequacyAggregatesModel): Promise<number> {
    return await this.makePost<number>(`exports/adequacy-aggregates`, model);
  }

  public async postExportAdequacySummaries(model: ExportAdequacySummariesModel): Promise<number> {
    return await this.makePost<number>(`exports/adequacy-summaries`, model);
  }

  public async postExportAdequacyServicingProviders(model: ExportAdequacyServicingProvidersModel): Promise<number> {
    return await this.makePost<number>(`exports/adequacy-servicing-providers`, model);
  }

  public async postExportZipSummaries(model: ZipSummariesReportModel): Promise<number> {
    return await this.makePost<number>(`exports/zip-summaries`, model);
  }

  public async postExportClosestProvider(model: ClosestProviderReportModel): Promise<number> {
    return await this.makePost<number>(`exports/closest-provider`, model);
  }

  public async postExportAdequacySpecialtySummaries(model: ExportAdequacySpecialtySummaryProjectModel): Promise<number> {
    return await this.makePost<number>(`exports/adequacy-specialty-summaries`, model);
  }

  // Attestation
  public async postExportAttestationAggregates(model: ExportAttestationAggregatesModel): Promise<number> {
    return await this.makePost<number>(`exports/attestation-aggregates`, model);
  }

  public async postExportAttestationSummaries(model: ExportAttestationSummariesModel): Promise<number> {
    return await this.makePost<number>(`exports/attestation-summaries`, model);
  }

  public async postExportAttestationServicingProviders(model: ExportAttestationServicingProvidersModel): Promise<number> {
    return await this.makePost<number>(`exports/attestation-servicing-providers`, model);
  }

  // Composite
  public async postExportCompositeAggregates(model: ExportCompositeAggregatesModel): Promise<number> {
    return await this.makePost<number>(`exports/composite-aggregates`, model);
  }

  public async postExportCompositeSummaries(model: ExportCompositeSummariesModel): Promise<number> {
    return await this.makePost<number>(`exports/composite-summaries`, model);
  }

  public async postExportCompositeServicingProviders(model: ExportCompositeServicingProvidersModel): Promise<number> {
    return await this.makePost<number>(`exports/composite-servicing-providers`, model);
  }

  // Summaries
  public async postSummariesDetailProviders(model: ExportSummariesDetailProvidersRequestModel): Promise<number> {
    return await this.makePost<number>(`exports/summaries-detail-providers`, model);
  }

  // User Login Statistics
  public async postUserLoginStatistics(model: ExportUserLoginStatisticsRequestModel): Promise<number> {
    return await this.makePost<number>(`exports/user-login-statistics`, model);
  }

  // Report Settings
  public async getAllUserReportSettings(): Promise<ReportSettingsModel[]> {
    return await this.makeGet<ReportSettingsModel[]>(`exports/report-settings/all`);
  }

  public async saveReportSettings(model: ReportSettingsModel): Promise<ReportSettingsModel> {
    return await this.makePost<ReportSettingsModel>(`exports/report-settings`, model);
  }

  public async updateReportSettings(models: ReportSettingsModel[]): Promise<boolean> {
    return await this.makePut<boolean>(`exports/report-settings`, models);
  }

  public async deleteReportSettings(ids: number[]): Promise<boolean> {
    return await this.makePost<boolean>(`exports/report-settings/delete`, ids);
  }

  // Reports Page
  public async getAvailableReports(startDate: string, endDate: string, searchColumn: string, searchValue: string, searchValue2?: string): Promise<ReportsUIModel[]> {
    return await this.makeGet<ReportsUIModel[]>(`exports/getreportsbyuserid?startDate=${startDate}&endDate=${endDate}&searchColumn=${searchColumn}&searchValue=${searchValue}&searchValue2=${searchValue2}`);
  }

  public async getAvailableSharedReports(startDate: string, endDate: string, searchColumn: string, searchValue: string, searchValue2?: string): Promise<ReportsUIModel[]> {
    return await this.makeGet<ReportsUIModel[]>(`exports/getsharedreportsbyuserid?startDate=${startDate}&endDate=${endDate}&searchColumn=${searchColumn}&searchValue=${searchValue}&searchValue2=${searchValue2}`);
  }

  public async getAvailableFilterCriteria(startDate: string, endDate: string): Promise<AvailableFilterCriteria> {
    return await this.makeGet<AvailableFilterCriteria>(`exports/getavailablefiltercriteria?startDate=${startDate}&endDate=${endDate}`);
  }

  public getProjectReportLink(projectId: number, reportId: number): string {
    return `${window.apiEndpointV2_0}/exports/download/${projectId}/${reportId}`;
  }

  public getReportLink(reportId: number): string {
    return `${window.apiEndpointV2_0}/exports/download/${reportId}`;
  }

  // Export Job Control
  public async cancelReportJob(reportId: number): Promise<boolean> {
    return await this.makePost<boolean>(`exports/cancelreportjob`, reportId);
  }

  // Bulk/Volume Reports
  public async postVolumeAdequacyAggregates(requestModel: ExportAdequacyVolumeAggregateRequestModel): Promise<number> {
    return await this.makePost<number>(`exports/adequecy-bulk-aggregate`, requestModel);
  }

  public postVolumeSummaries(requestModel: VolumeSummariesRequestModel): Promise<void> {
    return this.makePost("exports/bulk-summaries", requestModel);
  }

  public postVolumeServicingProviders(requestModel: VolumeServicingProvidersRequestModel): Promise<void> {
    return this.makePost("exports/bulk-servicing-providers", requestModel);
  }

  public postVolumeZipSummaries(requestModel: VolumeZipSummariesRequestModel): Promise<void> {
    return this.makePost("exports/bulk-zip-summaries", requestModel);
  }

  public async postExportCCIIOProviders(requestModel: ExportCCIIOProviderModel): Promise<number> {
    return await this.makePost<number>(`exports/cciio-providers`, requestModel);
  }

  public async postExportCCIIOProviderValidations(requestModel: ExportCCIIODataValidationsModel): Promise<number> {
    return await this.makePost<number>(`exports/cciio-validation-data-integrity`, requestModel);
  }

  public async postExportCCIIOProviderValidationsSummary(requestModel: ExportCCIIODataValidationsModel): Promise<number> {
    return await this.makePost<number>(`exports/cciio-provider-validation-summaries`, requestModel);
  }

  public postVolumeSpecialtySummaries(requestModel: VolumeAdequacySpecialtySummariesRequestModel): Promise<void> {
    return this.makePost("exports/bulk-specialty-summaries", requestModel);
  }

  public async getVolumeTargetRowCount(rowCount: number): Promise<number> {
    return await this.makeGet(`exports/volume-target-row-count?rowCount=${rowCount}`);
  }

  public async getVolumeTargetUploadLimit(): Promise<number> {
    return await this.makeGet('exports/volume-target-upload-limit');
  }

  public async getNetworkServicingProviderUploadLimit(): Promise<number> {
    return await this.makeGet('exports/network-provider-report-limit');
  }

  public async getProspectMarketServicingProviderUploadLimit(): Promise<number> {
    return await this.makeGet('exports/prospect-market-provider-report-limit');
  }

  public async getSharedUsers(exportType: string, customProjectIds: number[], networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    networkProviderFilterType: NetworkProviderFilterType, aggregateType: AdequacyAggregateType): Promise<SharedUserModel[]> {
    const query = {
      aggregateType,
      networkAssessmentAnalysisType,
      networkProviderFilterType
    };

    return await this.makePost<SharedUserModel[]>(`exports/sharedUsers/exportType/${exportType}?${querystringify(query)}`, customProjectIds);
  }

  public async postExportPciDashboardDetails(projectId: number, runConfigurationId: number, requestModel: PciDashboardFiltersPayload): Promise<number> {
    return await this.makePost<number>(`exports/project/${projectId}/runconfiguration/${runConfigurationId}/pci-dashboard-details`, requestModel);
  }

  public async postExportPciCrossProject(requestModel: ExportPciCrossProjectRequestModel): Promise<number> {
    return await this.makePost<number>(`exports/pci-cross-project`, requestModel);
  }

  public async postExportBenchmarkingDashboardDetails(requestModel: ExportBenchmarkingDashboardDetailsModel): Promise<number> {
    return await this.makePost<number>(`exports/benchmarking-dashboard-details`, requestModel);
  }

  public async exportZipAnalysisResults(projectId: number, runConfigurationId: number, payload: ExportZipAnalysisRequestModel): Promise<void> {
    return await this.makePost(`exports/project/${projectId}/runconfiguration/${runConfigurationId}/zip-analysis-results`, payload);
  }

  public async exportZipClassAggregateResults(projectId: number, runConfigurationId: number, payload: ExportZipAnalysisRequestModel): Promise<void> {
    return await this.makePost(`exports/project/${projectId}/runconfiguration/${runConfigurationId}/zip-class-aggregate-results`, payload);
  }

  public async exportProviders(requestModel: ExportProvidersModel): Promise<void> {
    return await this.makePost(`exports/provider-export`, requestModel);
  }

  public async generateBulkSpecialtyGroupExceptionReport(requestModel: ExportBulkSpecialtyGroupExceptionsModel): Promise<number> {
    return await this.makePost<number>(`exports/bulk-exceptions`, requestModel);
  }

  /* Report Customization Endpoints */
  /* This endpoint is for the dropdown on the report customization page */
  public async getColumnCustomizationPresets(reportType: ReportType): Promise<ColumnConfigurationPresetOption[]> {
    return await this.makeGet(`exports/${reportType}/presets`);
  }

  /* And this endpoint is for the list of presets on the manage presets page */
  public async getReportPresets(): Promise<ReportPresetsApiResponse[]> {
    return this.makeGet("exports/presets");
  }

  public async getColumnConfigurationPresetDetails(reportType: ReportType, presetId: number): Promise<ReportPreset> {
    return this.makeGet(`exports/${reportType}/presets/${presetId}`);
  }

  /* Get export columns endpoints */
  public getAdequacyServicingProvidersColumns = async (model: ExportAdequacyServicingProvidersModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>(`exports/adequacy-servicing-providers/columns`, model);
  }

  public getAdequacyAggregatesColumns = async (model: ExportAdequacyAggregatesModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>(`exports/adequacy-aggregates/columns`, model);
  }

  public getAdequacySpecialtySummaryColumns = async (model: ExportAdequacySpecialtySummaryProjectModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>(`exports/adequacy-specialty-summaries/columns`, model);
  }

  public getClosestProviderColumns = async (model: ClosestProviderReportModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>(`exports/closest-provider/columns`, model);
  }

  public getZipSummariesColumns = async (model: ZipSummariesReportModel): Promise<ExportColumn[]> => {
    return await this.makePost<ExportColumn[]>(`exports/zip-summaries/columns`, model);
  }
  /* End get columns endpoints */

  public async saveNewColumnConfigurationPreset(preset: ColumnConfigurationPreset): Promise<void> {
    return await this.makePost("exports/save-preset", preset);
  }

  public async editColumnConfigurationPreset(preset: ColumnConfigurationPreset, reportCustomizationId: number): Promise<void> {
    return await this.makePost(`exports/presets/${reportCustomizationId}`, preset);
  }
}
